import {
  Component,
  computed,
  inject,
  signal,
  ViewChild,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { MenuItem } from 'primeng/api'
import { UserModel } from '../../../../core/models/user.model'
import { AsyncPipe, CommonModule, NgIf } from '@angular/common'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { ButtonModule } from 'primeng/button'
import { StepsModule } from 'primeng/steps'
import { CardModule } from 'primeng/card'
import { AffairScheduleTimeSelectComponent } from '../affair-schedule-time-select/affair-schedule-time-select.component'
import {
  BehaviorSubject,
  finalize,
  Observable,
  Subscription,
  switchMap,
} from 'rxjs'
import { ConfirmationService } from 'primeng/api'
import { ConfirmDialogModule } from 'primeng/confirmdialog'
import { AffairsService } from '../../services/affairs.service'
import { UserService } from '../../../users/services/user.service'
import { AffairScheduleInterventionCreateComponent } from '../affair-schedule-intervention-create/affair-schedule-intervention-create.component'
import { AffairScheduleTechniciansSelectComponent } from '../affair-schedule-technicians-select/affair-schedule-technicians-select.component'
import { InterventionsService } from '../../../interventions/services/interventions.service'
import { AffairScheduleInterventionScheduleComponent } from '../affair-schedule-intervention-schedule/affair-schedule-intervention-schedule.component'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ToastService } from '../../../../shared/services/toast.service'
import { AffairScheduleProductsSelectComponent } from '../affair-schedule-products-select/affair-schedule-products-select.component'
import { ProductsService } from '../../../products/services/products.service'
import { environment } from '../../../../../environments/environment'

@Component({
  selector: 'app-affair-schedule-steps',
  standalone: true,
  templateUrl: './affair-schedule-steps.component.html',
  styleUrls: ['./affair-schedule-steps.component.scss'],
  imports: [
    CommonModule,
    StepsModule,
    AffairScheduleTechniciansSelectComponent,
    AffairScheduleTimeSelectComponent,
    AffairScheduleInterventionScheduleComponent,
    AffairScheduleInterventionCreateComponent,
    AffairScheduleProductsSelectComponent, // Add new component
    ProgressSpinnerModule,
    ButtonModule,
    AsyncPipe,
    NgIf,
    CardModule,
    ConfirmDialogModule,
  ],
  providers: [ConfirmationService],
})
export class AffairScheduleStepsComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private fb = inject(FormBuilder)
  private toastService = inject(ToastService)

  public scheduleForm: FormGroup
  public interventionForm: FormGroup
  private localStorageKey: string = ''
  public mlRef = this.route.snapshot.params['mlRef']

  public activeStep = signal(0)
  public steps: MenuItem[] = [
    { label: 'Sélection des produits' }, // New first step
    { label: 'Sélection du/des technicien(s)' },
    { label: "Sélection d'un créneau horaire" },
    { label: 'Programmation' },
    { label: 'Informations complémentaires' },
  ]
  selectedTechnicians: UserModel[] = []
  public dateRange = signal<Date[]>([])

  public users$: Observable<UserModel[]>
  public loadingUsers = false
  private filtersSubject = new BehaviorSubject<any>({
    departmentsCode: [],
    search: '',
    productsNames: [],
  })
  public filters$ = this.filtersSubject.asObservable()

  public formattedDateRange = computed(() => {
    const [start, end] = this.dateRange()
    if (!start || !end) return 'Non défini'
    return `${this.formatDate(start)} - ${this.formatDate(end)}`
  })
  public affair$: Subscription
  public affair: any
  public affairLoading = true
  public droppedDetails: any
  public closestInterventions: any[] = [] // New property to store closest interventions
  public techniciansByRoute: {
    technician: any
    distance_km: number
    duration_min: number | null
    distance_type: 'route' | 'haversine'
  }[] = [] // Store technicians by route

  @ViewChild(AffairScheduleInterventionCreateComponent)
  interventionCreateComponent: AffairScheduleInterventionCreateComponent

  @ViewChild(AffairScheduleInterventionScheduleComponent)
  interventionScheduleComponent: AffairScheduleInterventionScheduleComponent

  // Ajout de la référence au composant enfant pour pouvoir appeler loadCalendarEvents
  @ViewChild(AffairScheduleTimeSelectComponent, { static: false })
  affairScheduleTimeSelectComponent?: AffairScheduleTimeSelectComponent

  public submittingIntervention = false
  public interventionError = ''
  public interventionFormLoading = false
  public hasDroppedEvent = false
  public scheduleData: any = null
  public calendarReset = false

  private savedFormData: any = {}

  // Add new properties for map reset
  public resetMapFlag = false

  // Add a product reset flag
  public productResetFlag = false

  // Add new properties for products
  public products: any[] = []
  public selectedProducts: any[] = []
  public loadingProducts = false
  public productGroups: any[] = []

  // Add new property to track when returning to calendar step
  public returningToCalendarStep = false

  // Add property to store users for intervention schedule component
  public interventionScheduleUsers: any[] = []
  private loadingInterventionScheduleUsers = false

  // Add public property for pre-selected slot
  public preSelectedSlotData: any = null

  // Add property for selected intervention (step 2)
  public selectedIntervention: any = null

  // Add property for batch indicators (step 2)
  public batchIndicatorsData: { [key: number]: any } = {}

  public constructor(
    private affairsService: AffairsService,
    private usersService: UserService,
    private interventionsService: InterventionsService,
    private productsService: ProductsService, // Add this service
    private confirmationService: ConfirmationService
  ) {
    this.users$ = this.filters$.pipe(
      switchMap((filters) => {
        this.loadingUsers = true
        return this.usersService
          .getUsersWithFilters(filters)
          .pipe(finalize(() => (this.loadingUsers = false)))
      })
    )

    this.initScheduleForm()
    this.initInterventionForm()
  }

  private initScheduleForm(): void {
    this.scheduleForm = this.fb.group({
      startDate: [new Date(), Validators.required],
      duration: [null],
      isTimeSlot: [false],
      startTimeSlot: [null],
      endTimeSlot: [null],
      techniciansIds: [[]],
      userAssistantId: [null],
      isConfirmed: [true],
      dateRange: [this.dateRange()],
    })

    this.scheduleForm.valueChanges.subscribe((formData) => {
      this.scheduleData = formData
      this.saveToLocalStorage()
    })
  }

  private initInterventionForm(): void {
    this.interventionForm = this.fb.group({
      mlRef: [this.mlRef],
      name: ['', Validators.required],
      generatedDescription: ['', Validators.required],
      description: '',
      conformityId: null,
      worksSupervisorId: null,
      isDeliveredHouse: false,
      isWaitingCheck: false,
      recoverCheck: false,
      isOffice: false,
      doNotShift: false,
      doNotShiftReason: '',
      contacts: null,
      affairsContacts: null,
      workSupervisorId: null,
      erpSource: null,
      affairProducts: [[]],
      productData: null,
    })

    this.interventionForm.valueChanges.subscribe(() => {
      this.saveToLocalStorage()
    })
  }

  public updateFilters(filters: any): void {
    this.filtersSubject.next(filters)
    this.saveToLocalStorage()
  }

  public ngOnInit(): void {
    this.localStorageKey = `affair-schedule-${this.mlRef}`

    // Start loading users for intervention schedule early in the background
    this.loadInterventionScheduleUsers()

    this.affair$ = this.affairsService
      .getAffairById(this.mlRef)
      .pipe(
        finalize(() => {
          this.affairLoading = false
          this.loadProducts() // Load products
          this.restoreFromLocalStorage()
        })
      )
      .subscribe((affair) => {
        this.affair = affair
      })

    window.addEventListener('beforeunload', () => {
      this.saveToLocalStorage()
    })
  }

  ngOnDestroy(): void {
    if (this.affair$) {
      this.affair$.unsubscribe()
    }

    window.removeEventListener('beforeunload', () => {
      this.saveToLocalStorage()
    })
  }

  private saveToLocalStorage(): void {
    try {
      const step3Data =
        this.activeStep() === 2 ? this.scheduleForm.value : this.scheduleData

      const step4Data =
        this.activeStep() === 3 && this.interventionCreateComponent
          ? this.interventionCreateComponent.getFormData()
          : null

      const currentFormData = {
        ...this.savedFormData,
        step3: this.savedFormData?.step3 || step3Data,
        step4: this.savedFormData?.step4 || step4Data,
      }

      if (step3Data) currentFormData.step3 = step3Data
      if (step4Data) currentFormData.step4 = step4Data

      const state = {
        activeStep: this.activeStep(),
        selectedTechnicians: this.selectedTechnicians,
        dateRange: this.dateRange().map((date) => {
          if (date && date instanceof Date && !isNaN(date.getTime())) {
            return date.toISOString()
          }
          return null
        }),
        droppedDetails: this.droppedDetails,
        hasDroppedEvent: this.hasDroppedEvent,
        scheduleData: this.scheduleData || step3Data,
        scheduleFormValue: this.scheduleForm.value,
        interventionFormValue: this.interventionForm.value,
        filters: this.filtersSubject.getValue(),
        formData: currentFormData,
        closestInterventions: this.closestInterventions, // Save closest interventions
        techniciansByRoute: this.techniciansByRoute, // Save technicians by route
        selectedIntervention: this.selectedIntervention, // Save selected intervention
        preSelectedSlot: this.preSelectedSlotData, // Save pre-selected slot
        batchIndicatorsData: this.batchIndicatorsData, // Save batch indicators
        selectedProducts: this.selectedProducts,
        productGroups: this.productGroups,
      }
      localStorage.setItem(this.localStorageKey, JSON.stringify(state))

      this.savedFormData = currentFormData
    } catch (error) {
      // Keep error logging for debugging purposes
      console.error('Error saving state to localStorage:', error)
    }
  }

  private saveCurrentStepData(): void {
    try {
      const currentStep = this.activeStep()

      this.savedFormData = { ...this.savedFormData }

      if (currentStep === 0) {
      } else if (currentStep === 1) {
        this.savedFormData = {
          ...this.savedFormData,
          step2: {
            droppedDetails: this.droppedDetails,
            hasDroppedEvent: this.hasDroppedEvent,
          },
        }
      } else if (currentStep === 2) {
        const scheduleData = this.scheduleForm.value
        if (scheduleData) {
          this.scheduleData = scheduleData
          this.savedFormData = {
            ...this.savedFormData,
            step3: scheduleData,
          }
        }
      } else if (currentStep === 3 && this.interventionCreateComponent) {
        const createData = this.interventionCreateComponent.getFormData()
        if (createData) {
          this.savedFormData = {
            ...this.savedFormData,
            step4: JSON.parse(JSON.stringify(createData)),
          }
        }
      }
    } catch (error) {
      console.error('Error saving current step data:', error)
    }
  }

  nextStep(): void {
    if (this.activeStep() < this.steps.length - 1) {
      // Check if we're on the products step and there are no products selected
      if (
        this.activeStep() === 0 &&
        (!this.selectedProducts || this.selectedProducts.length === 0)
      ) {
        alert('Veuillez sélectionner au moins un produit')
        return
      }

      // Updated checks for other steps - note the index shift
      if (this.activeStep() === 1 && !this.isTechniciansSelected) {
        return
      }

      if (this.activeStep() === 2 && !this.hasDroppedEvent) {
        alert("Veuillez d'abord déposer un produit sur le calendrier")
        return
      }

      if (this.activeStep() === 3) {
        if (!this.interventionScheduleComponent?.isFormValid()) {
          alert(
            'Veuillez remplir correctement les informations de programmation'
          )
          return
        }
        this.scheduleData = this.interventionScheduleComponent.getFormData()
      }

      this.saveCurrentStepData()

      const currentStep = this.activeStep()
      const nextStep = currentStep + 1

      // Si nous allons à l'étape finale (étape 3), s'assurer que l'erpSource est défini
      if (nextStep === 3) {
        // S'assurer que l'erpSource est défini pour la génération de nom/description
        const currentErpSource = this.interventionForm.get('erpSource')?.value
        if (!currentErpSource && this.selectedTechnicians.length > 0) {
          this.interventionForm.patchValue({
            erpSource: this.resolveErpSource(
              this.selectedTechnicians[0].erpSource || 'mylink'
            ),
          })
        }

        // Trigger name/description generation in the child component when we reach step 4
        setTimeout(() => {
          if (this.interventionCreateComponent) {
            this.interventionCreateComponent.prepareAndTriggerGeneration()
          }
        }, 500)
      }

      this.activeStep.update((step) => step + 1)

      this.saveToLocalStorage()

      setTimeout(() => {
        this.applyFormDataToCurrentStep()
      }, 100)
    }
  }

  previousStep(): void {
    if (this.activeStep() > 0) {
      this.saveCurrentStepData()
      this.saveToLocalStorage()

      // Set flag if returning to calendar step
      this.returningToCalendarStep =
        this.activeStep() === 3 && this.hasDroppedEvent

      this.activeStep.update((step) => step - 1)
      this.interventionError = ''

      setTimeout(() => {
        this.applyFormDataToCurrentStep()
      }, 100)
    }
  }

  private applyFormDataToCurrentStep(): void {
    const currentStep = this.activeStep()

    if (currentStep === 0 && this.savedFormData?.step1) {
      // Step 1 restoration logic
    } else if (currentStep === 1 && this.savedFormData?.step2) {
      if (
        this.savedFormData.step2.droppedDetails &&
        this.droppedDetails === undefined
      ) {
        this.droppedDetails = this.savedFormData.step2.droppedDetails
        this.hasDroppedEvent = this.savedFormData.step2.hasDroppedEvent || false
      }
    } else if (currentStep === 2) {
      // For the calendar step, ensure dropped event is restored properly
      let attempts = 0
      const checkAndApply = setInterval(() => {
        if (this.interventionScheduleComponent && this.savedFormData?.step3) {
          this.interventionScheduleComponent.restoreFormData(
            this.savedFormData.step3
          )
          clearInterval(checkAndApply)
        } else if (++attempts >= 10) {
          clearInterval(checkAndApply)
        }
      }, 100)

      // Reset the returning flag after a delay
      setTimeout(() => {
        this.returningToCalendarStep = false
      }, 500)
    } else if (currentStep === 3) {
      let attempts = 0
      const checkAndApply = setInterval(() => {
        if (this.interventionCreateComponent && this.savedFormData?.step4) {
          this.interventionCreateComponent.restoreFormData(
            this.savedFormData.step4
          )
          clearInterval(checkAndApply)
        } else if (++attempts >= 10) {
          clearInterval(checkAndApply)
        }
      }, 100)
    }
  }

  onTechniciansSelected(technicians: UserModel[]) {
    this.selectedTechnicians = technicians

    // Mettre à jour l'erpSource dans le formulaire d'intervention
    if (technicians && technicians.length > 0 && technicians[0].erpSource) {
      this.interventionForm.patchValue({
        erpSource: this.resolveErpSource(technicians[0].erpSource),
      })
    }

    this.saveToLocalStorage()
  }

  // Add handler for closest interventions
  onClosestInterventionsChanged(interventions: any[]) {
    this.closestInterventions = interventions
    this.saveToLocalStorage()
  }

  // Add handler for technicians by route
  onTechniciansByRouteChanged(
    technicians: {
      technician: any
      distance_km: number
      duration_min: number | null
      distance_type: 'route' | 'haversine'
    }[]
  ) {
    this.techniciansByRoute = technicians
    this.saveToLocalStorage()
  }

  // Add handler for selected intervention changes
  onSelectedInterventionChanged(intervention: any) {
    this.selectedIntervention = intervention
    this.saveToLocalStorage()
  }

  // Add handler for pre-selected slot changes
  onPreSelectedSlotChanged(slot: any) {
    this.preSelectedSlotData = slot
    this.saveToLocalStorage()
  }

  // Add handler for batch indicators changes
  onBatchIndicatorsChanged(indicators: { [key: number]: any }) {
    this.batchIndicatorsData = indicators
    this.saveToLocalStorage()
  }

  // Add handler for pre-selected slots
  onSlotPreSelected(slotData: any) {
    if (slotData) {
      // Store the pre-selected slot information
      this.savedFormData.preSelectedSlot = slotData
      this.preSelectedSlotData = slotData

      // Clear any existing dropped event details since the pre-selected slot replaces it
      this.droppedDetails = null
      this.hasDroppedEvent = false

      // Also clear from saved form data
      if (this.savedFormData.step2) {
        this.savedFormData.step2.droppedDetails = null
        this.savedFormData.step2.hasDroppedEvent = false
      }

      this.saveToLocalStorage()

      // Log for debugging
      console.log('Slot pre-selected, cleared dropped event details:', slotData)
    } else {
      // Clear the pre-selected slot
      delete this.savedFormData.preSelectedSlot
      this.preSelectedSlotData = null
      this.saveToLocalStorage()
    }
  }

  onDateRangeSelectedFromStep1(dates: [Date, Date]): void {
    if (!dates || !dates[0] || !dates[1]) {
      return
    }

    const start = new Date(dates[0])
    const end = new Date(dates[1])

    start.setHours(0, 0, 0, 0)
    end.setHours(23, 59, 59, 999)

    const normalizedDates: Date[] = [start, end]

    this.dateRange.set(normalizedDates)
    this.saveToLocalStorage()
  }

  onDateRangeSelected(dates: Date[]): void {
    if (!dates || !Array.isArray(dates) || dates.length !== 2) {
      return
    }

    try {
      const start = this.createValidDate(dates[0])
      const end = this.createValidDate(dates[1])

      if (!start || !end) {
        return
      }

      start.setHours(0, 0, 0, 0)
      end.setHours(23, 59, 59, 999)

      this.dateRange.set([start, end])
      this.saveToLocalStorage()
      // Recharger les événements du calendrier si on est à l'étape 3
      if (this.activeStep() === 2) {
        // Attendre un petit délai pour s'assurer que le composant est disponible
        setTimeout(() => {
          if (this.affairScheduleTimeSelectComponent) {
            this.affairScheduleTimeSelectComponent.loadCalendarEvents()
          }
        }, 100)
      }
    } catch (error) {
      console.error('Error processing dates from Step 2:', error)
    }
  }

  private createValidDate(input: any): Date | null {
    if (!input) return null

    try {
      const date = new Date(input)
      return isNaN(date.getTime()) ? null : date
    } catch (e) {
      return null
    }
  }

  onDropDetails(event: any) {
    // Si on reçoit null, réinitialiser les détails de l'événement
    if (!event) {
      this.droppedDetails = null
      this.hasDroppedEvent = false
      this.savedFormData.step2 = {
        droppedDetails: null,
        hasDroppedEvent: false,
      }
      this.saveToLocalStorage()
      return
    }

    // Nouveau code pour gérer la réception d'un événement
    this.droppedDetails = event
    this.hasDroppedEvent = !!event

    if (!this.savedFormData.step2) {
      this.savedFormData.step2 = {}
    }
    this.savedFormData.step2.droppedDetails = event
    this.savedFormData.step2.hasDroppedEvent = !!event

    if (this.scheduleForm && event?.date) {
      this.scheduleForm.patchValue({
        startDate: event.date,
      })

      if (event?.productsData) {
        // Utiliser la durée totale de l'événement plutôt que d'un seul produit
        const durationMinutes =
          event.duration ||
          event.productsData.reduce(
            (sum: number, product: any) =>
              sum + (product.product?.duration || 0),
            0
          )

        const hours = Math.floor(durationMinutes / 60)
        const minutes = durationMinutes % 60

        const durationDate = new Date()
        durationDate.setHours(hours)
        durationDate.setMinutes(minutes)
        durationDate.setSeconds(0)

        this.scheduleForm.patchValue({
          duration: durationDate,
        })
      }
    }

    this.saveToLocalStorage()

    // Appeler generateNameAndDescription du composant enfant quand droppedDetails devient non nul
    /*console.log('getGe,nerateNameAndDescription appelé depuis onDropDetails')
    if (
      this.droppedDetails &&
      this.interventionCreateComponent &&
      typeof this.interventionCreateComponent.generateNameAndDescription ===
        'function'
    ) {
      this.interventionCreateComponent.generateNameAndDescription()
    }*/
  }

  onSubmit(): void {
    alert('Données soumises avec succès !')
    this.clearSavedState()
  }

  get isTechniciansSelected() {
    return this.selectedTechnicians.length > 0
  }

  private formatDate(date: Date): string {
    return `${date.getFullYear()}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
  }

  onGoToPreviousStep() {
    this.previousStep()
  }

  onInterventionCreated(intervention: any) {
    this.clearSavedState()
    this.toastService.showSuccess('Intervention créée avec succès')
    this.router.navigate(['/affair', this.mlRef])
  }

  onSubmitIntervention(): void {
    if (!this.interventionCreateComponent) {
      console.error("Composant de création d'intervention non disponible")
      return
    }

    if (!this.interventionCreateComponent.isFormValid()) {
      this.interventionError = 'Veuillez remplir tous les champs obligatoires'
      return
    }

    this.submittingIntervention = true
    this.interventionError = ''

    this.interventionCreateComponent
      .submitForm()
      .then((response) => {
        // Clear local storage immediately after successful submission
        this.clearSavedState()

        // Open the affair on Eros in a new tab
        const erpSource =
          response.data?.usersTechnicians?.[0]?.erpSource ||
          this.interventionForm.get('erpSource')?.value ||
          'mylink'
        this.openAffairOnEros(erpSource)

        // Show success message and navigate to intervention view
        this.toastService.showSuccess("L'intervention a été créée avec succès")

        const interventionId = response.data?.id

        this.router.navigate([
          '/intervention',
          interventionId,
          'view',
          erpSource,
        ])
      })
      .catch((err) => {
        console.error('Error creating intervention:', err)
        this.interventionError =
          typeof err === 'string'
            ? err
            : err.error?.message ||
              "Une erreur est survenue lors de la création de l'intervention"
      })
      .finally(() => {
        this.submittingIntervention = false
      })
  }

  private openAffairOnEros(erpSource: string): void {
    if (!this.affair) return
    const affairId = this.affair.srcErp?.[erpSource]?.id

    if (!affairId) return

    if (erpSource === 'abm') {
      window.open(
        environment.abmUrl + 'affairs/' + affairId + '/show',
        '_blank'
      )
    } else if (erpSource === 'nrj') {
      window.open(
        environment.nrjUrl + 'affairs/' + affairId + '/show',
        '_blank'
      )
    }
  }

  onScheduleChanged(scheduleData: any) {
    this.scheduleData = scheduleData
    this.saveToLocalStorage()
  }

  private clearSavedState(): void {
    try {
      localStorage.removeItem(this.localStorageKey)
    } catch (error) {
      console.error('Error clearing state from localStorage:', error)
    }
  }

  private restoreFromLocalStorage(): void {
    try {
      const savedState = localStorage.getItem(this.localStorageKey)
      if (savedState) {
        const state = JSON.parse(savedState)

        if (state.activeStep !== undefined) {
          this.activeStep.set(state.activeStep)
        }

        if (
          state.selectedTechnicians &&
          Array.isArray(state.selectedTechnicians)
        ) {
          this.selectedTechnicians = state.selectedTechnicians
        }

        if (state.dateRange && Array.isArray(state.dateRange)) {
          const restoredDates = state.dateRange
            .map((dateString: string) =>
              dateString ? new Date(dateString) : null
            )
            .filter(Boolean)

          if (restoredDates.length === 2) {
            this.dateRange.set(restoredDates)
          }
        }

        if (state.droppedDetails) {
          this.droppedDetails = state.droppedDetails
          this.hasDroppedEvent = state.hasDroppedEvent || false
        }

        if (state.scheduleData) {
          this.scheduleData = state.scheduleData
        }

        if (state.scheduleFormValue) {
          const formValue = { ...state.scheduleFormValue }

          // More robust date conversion
          try {
            if (formValue.startDate) {
              formValue.startDate = new Date(formValue.startDate)
              console.log('Restored startDate:', formValue.startDate)
            }

            // Convert other date fields
            if (formValue.duration)
              formValue.duration = new Date(formValue.duration)
            if (formValue.startTimeSlot)
              formValue.startTimeSlot = new Date(formValue.startTimeSlot)
            if (formValue.endTimeSlot)
              formValue.endTimeSlot = new Date(formValue.endTimeSlot)

            // Apply the schedule form values - use setValue for complete replacement
            this.scheduleForm.setValue(formValue)
          } catch (error) {
            console.error('Error converting form date values:', error)
            // Partial restore attempt if full restore fails
            this.scheduleForm.patchValue(formValue)
          }
        }

        this.savedFormData = state.formData || {}

        if (state.interventionFormValue) {
          try {
            const formValue = state.interventionFormValue

            this.interventionForm.patchValue({
              mlRef: formValue.mlRef,
              description: formValue.description,
              conformityId: formValue.conformityId,
              worksSupervisorId: formValue.worksSupervisorId,
              isDeliveredHouse: formValue.isDeliveredHouse,
              isWaitingCheck: formValue.isWaitingCheck,
              recoverCheck: formValue.recoverCheck,
              isOffice: formValue.isOffice,
              erpSource:
                formValue.erpSource ||
                this.selectedTechnicians?.[0]?.erpSource ||
                'mylink',
            })

            if (formValue.productData) {
              this.interventionForm
                .get('productData')
                ?.setValue(formValue.productData)
            }

            if (Array.isArray(formValue.affairProducts)) {
              this.interventionForm
                .get('affairProducts')
                ?.setValue(formValue.affairProducts)
            }
          } catch (error) {
            console.error('Error restoring intervention form values:', error)
          }
        } else if (this.selectedTechnicians?.length > 0) {
          // Ensure erpSource is set even if no saved form
          this.interventionForm.patchValue({
            erpSource: this.selectedTechnicians[0].erpSource || 'mylink',
          })
        }

        // Restore closest interventions if available
        if (state.closestInterventions) {
          this.closestInterventions = state.closestInterventions
        }

        // Restore technicians by route if available
        if (state.techniciansByRoute) {
          this.techniciansByRoute = state.techniciansByRoute
        }

        // Restore selected intervention if available
        if (state.selectedIntervention) {
          this.selectedIntervention = state.selectedIntervention
        }

        // Restore pre-selected slot if available
        if (state.preSelectedSlot) {
          this.preSelectedSlotData = state.preSelectedSlot
        }

        // Restore batch indicators if available
        if (state.batchIndicatorsData) {
          this.batchIndicatorsData = state.batchIndicatorsData
        }

        // Restore selected products
        if (state.selectedProducts && Array.isArray(state.selectedProducts)) {
          this.selectedProducts = state.selectedProducts
        }

        // Restore product groups
        if (state.productGroups && Array.isArray(state.productGroups)) {
          this.productGroups = state.productGroups
        }
      }
    } catch (error) {
      console.error('Error restoring state from localStorage:', error)
    }
  }

  onFormStateChanged(event: { valid: boolean; data: any }): void {
    // Form state tracking
  }

  onInterventionFormStateChanged(event: {
    valid: boolean
    data: any
    isLoading?: boolean
  }): void {
    // Intervention form state tracking
    this.interventionFormLoading = event.isLoading ?? false
  }

  onInterventionFormError(error: string): void {
    this.interventionError = error
    console.error('Intervention form error:', error)
  }

  /**
   * Réinitialise complètement le formulaire et revient à la première étape
   */
  resetForm(): void {
    this.confirmationService.confirm({
      message:
        'Êtes-vous sûr de vouloir réinitialiser le formulaire ? Toutes les données saisies seront perdues.',
      header: 'Confirmation de réinitialisation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Oui, réinitialiser',
      rejectLabel: 'Non',
      accept: () => {
        this.calendarReset = true
        // Trigger map reset
        this.resetMapFlag = true
        // Trigger product selection reset
        this.productResetFlag = true

        this.activeStep.set(0)
        this.selectedTechnicians = []
        this.dateRange.set([])
        this.droppedDetails = null
        this.hasDroppedEvent = false
        this.scheduleData = null
        this.initScheduleForm()
        this.initInterventionForm()
        this.interventionError = ''
        this.savedFormData = {}

        // Reset product selections
        this.selectedProducts = []
        this.productGroups = []

        // Reset step 2 data (technician selection)
        this.closestInterventions = []
        this.techniciansByRoute = []
        this.selectedIntervention = null
        this.preSelectedSlotData = null
        this.batchIndicatorsData = {}

        // Update filters to remove product filters
        const currentFilters = this.filtersSubject.getValue()
        this.filtersSubject.next({
          ...currentFilters,
          productsNames: [],
        })

        this.clearSavedState()

        setTimeout(() => {
          this.calendarReset = false
          // Reset the map flag after a delay
          setTimeout(() => {
            this.resetMapFlag = false
          }, 200)

          // Reset the product selection flag after a delay
          setTimeout(() => {
            this.productResetFlag = false
          }, 200)
        }, 500)
      },
    })
  }

  // Add method to load products
  private loadProducts(): void {
    this.loadingProducts = true
    this.productsService
      .getSchedulableProductsByMlRef(this.mlRef)
      .pipe(finalize(() => (this.loadingProducts = false)))
      .subscribe(
        (products) => {
          this.products = products
        },
        (error) => {
          console.error('Error loading products:', error)
        }
      )
  }

  // Add handlers for product selection
  onProductSelectionChanged(products: any[]): void {
    this.selectedProducts = products

    // Add the selected product IDs to the filters
    const productNames = products.map((p) => p.product.name)

    // Update filters with the selected product IDs
    const currentFilters = this.filtersSubject.getValue()
    this.filtersSubject.next({
      ...currentFilters,
      productsNames: productNames, // Use productIds as the filter parameter
    })

    this.saveToLocalStorage()

    console.log('Selected products:', this.selectedProducts)
    console.log(this.activeStep())
    // Appeler generateNameAndDescription du composant enfant dès la première sélection de produits
    if (
      this.activeStep() === 0 &&
      this.interventionCreateComponent &&
      typeof this.interventionCreateComponent.generateNameAndDescription ===
        'function'
    ) {
      this.interventionCreateComponent.generateNameAndDescription()
    }
  }

  onProductGroupCreated(group: any): void {
    if (!group) return

    // Always replace the entire product groups array with just the current group
    this.productGroups = [group]
    this.saveToLocalStorage()
  }

  /**
   * Helper method to get product IDs from selected products for restoration
   */
  getRestoredProductIds(): number[] {
    if (!this.selectedProducts || !Array.isArray(this.selectedProducts))
      return []
    return this.selectedProducts.map((p) => Number(p.id))
  }

  // New method to preload users for intervention schedule
  private loadInterventionScheduleUsers(): void {
    if (this.loadingInterventionScheduleUsers) return
    this.loadingInterventionScheduleUsers = true

    this.usersService.getForSelect().subscribe({
      next: (users) => {
        this.interventionScheduleUsers = users
        this.loadingInterventionScheduleUsers = false
      },
      error: (error) => {
        this.loadingInterventionScheduleUsers = false
      },
    })
  }

  protected techniciansIncompatible = false

  /**
   * Résout l'erpSource en forçant 'nrj' si l'affaire est de type inspection (marketTypeId === 5)
   */
  private resolveErpSource(defaultSource: string): string {
    if (this.affair?.marketType?.id === 5) {
      return 'nrj'
    }
    return defaultSource
  }
}
